import { useCallback, useEffect, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import useDeepCompareEffect from 'react-use/lib/useDeepCompareEffect';

import api from 'services/api';
import { GET } from 'constants/api';
import { isArray, isEvent, isError } from 'helpers/simple';

const REQUEST_CACHE = {};
const DEFAULT_CACHE_INVALIDATION_TIME_IN_MINS = 30;

export const FETCH_POLICY = {
  CACHE_FIRST: 1,
  NETWORK_ONLY: 2,
};

const isFetching = ({ data, loading }) =>
  loading ? (isArray(data) ? !data.length : !data) : false;

const isCacheStale = invalidateAt => {
  if (!invalidateAt) return true;
  return invalidateAt < new Date().getTime();
};

const createCacheKey = ({ url, params }) => {
  if (!params) return url;
  return `${url}${JSON.stringify(params)}`;
};

const getRequestCacheInfo = cacheKey => {
  REQUEST_CACHE[cacheKey] = REQUEST_CACHE[cacheKey] || { invalidateAt: null };
  return REQUEST_CACHE[cacheKey];
};

const isLegibleToFetch = (cacheKey, fetchPolicy) => {
  if (fetchPolicy === FETCH_POLICY.NETWORK_ONLY) return true;
  const { invalidateAt } = getRequestCacheInfo(cacheKey);
  return isCacheStale(invalidateAt);
};

const setCacheInvalidationTime = (cacheKey, invalidationTimeInMin) => {
  const invalidationTime = Number(invalidationTimeInMin) || 0;
  getRequestCacheInfo(cacheKey).invalidateAt =
    new Date().getTime() + invalidationTime * 1000 * 60;
};

const getValidParams = params => (isEvent(params) ? undefined : params);

const mergeParams = (axiosOptions, params) => ({
  ...axiosOptions,
  params: { ...axiosOptions.params, ...getValidParams(params) },
});

const useQueryIndicator = (
  fetcher,
  {
    transformRes,
    onSuccess,
    onError,
    setData,
    axiosOptions,
    variables = {},
    invalidateCacheAfter = DEFAULT_CACHE_INVALIDATION_TIME_IN_MINS,
    fetchPolicy = FETCH_POLICY.CACHE_FIRST,
    refetchInterval,
    refetchOnMount,
  },
) => {
  const [indicator, setIndicator] = useState({ loading: false, error: null });
  const setError = useCallback(
    error =>
      setIndicator({
        error: isError(error) ? error : new Error(error),
      }),
    [indicator.error],
  );
  const doFetching = params => {
    const manualTrigger =
      !indicator.loading && (refetchInterval || refetchOnMount);
    setIndicator({ loading: true, error: null });
    const cacheKey = createCacheKey(
      mergeParams(axiosOptions, { ...params, ...variables }),
    );
    const shouldFetch =
      manualTrigger || isLegibleToFetch(cacheKey, fetchPolicy);
    if (!shouldFetch) {
      const cacheData = REQUEST_CACHE[cacheKey];
      setData(cacheData);
      setIndicator({ loading: false, error: null });
      if (onSuccess) onSuccess(cacheData, { setError });
      return;
    }
    fetcher(getValidParams(params))
      .then(res => {
        const data = transformRes ? transformRes(res) : res;
        setData(data);
        setIndicator({ loading: false, error: null });
        if (onSuccess) onSuccess(data, { setError });
        if (fetchPolicy === FETCH_POLICY.CACHE_FIRST) {
          REQUEST_CACHE[cacheKey] = data;
          setCacheInvalidationTime(cacheKey, invalidateCacheAfter);
        }
      })
      .catch(e => {
        setIndicator({ loading: false, error: e });
        if (onError) onError(e, { setData });
      });
  };
  return [doFetching, indicator];
};

const useFetch = ({ data, ...options }) => {
  const fetcher = async () => {
    if (options.stallQuery) throw new Error('query stalled');
    return api({ ...options.axiosOptions, method: GET });
  };

  const [doFetching, { loading, error }] = useQueryIndicator(fetcher, options);

  useDeepCompareEffect(() => {
    doFetching();
  }, [options.variables, options.axiosOptions.url]);

  useEffect(() => {
    if (options.refetchOnMount) {
      doFetching();
    }
  }, [options.refetchOnMount]);

  useEffect(() => {
    let timeout;
    clearInterval(timeout);
    if (options.refetchInterval) {
      timeout = setInterval(doFetching, options.refetchInterval);
    }
    return () => {
      clearInterval(timeout);
    };
  }, [options.refetchInterval]);

  return {
    loading,
    error,
    data,
    fetching: isFetching({ data, loading }),
    fetch: doFetching,
  };
};

const useLazyFetch = ({ data, ...options }) => {
  const fetcher = params =>
    api({ ...mergeParams(options.axiosOptions, params), method: GET });
  const [doFetching, { loading, error }] = useQueryIndicator(fetcher, options);
  return [
    doFetching,
    { loading, error, data, fetching: isFetching({ data, loading }) },
  ];
};

export const useReactiveQuery = (
  url,
  reactiveVar,
  {
    transformRes,
    axiosOptions,
    onSuccess,
    onError,
    fetchPolicy,
    invalidateCacheAfter,
    variables = {},
    stallQuery,
    refetchOnMount,
    refetchInterval,
  } = {},
) => {
  const data = useReactiveVar(reactiveVar);
  return useFetch({
    axiosOptions: { ...axiosOptions, url },
    data,
    setData: reactiveVar,
    onSuccess,
    onError,
    transformRes,
    fetchPolicy,
    invalidateCacheAfter,
    variables,
    stallQuery,
    refetchOnMount,
    refetchInterval,
  });
};

export const useLazyReactiveQuery = (
  url,
  reactiveVar,
  {
    transformRes,
    axiosOptions,
    onSuccess,
    onError,
    fetchPolicy,
    invalidateCacheAfter,
  } = {},
) => {
  const data = useReactiveVar(reactiveVar);
  return useLazyFetch({
    axiosOptions: { ...axiosOptions, url },
    data,
    setData: reactiveVar,
    onSuccess,
    onError,
    transformRes,
    fetchPolicy,
    invalidateCacheAfter,
  });
};

export const useQuery = (
  url,
  { transformRes, axiosOptions, onSuccess, onError, defaultData } = {},
) => {
  const [data, setData] = useState(defaultData);
  return useFetch({
    axiosOptions: { ...axiosOptions, url },
    data,
    setData,
    onSuccess,
    onError,
    transformRes,
    fetchPolicy: FETCH_POLICY.NETWORK_ONLY,
  });
};

export const useLazyQuery = (
  url,
  { transformRes, axiosOptions, onSuccess, onError, defaultData } = {},
) => {
  const [data, setData] = useState(defaultData);
  return useLazyFetch({
    axiosOptions: { ...axiosOptions, url },
    data,
    setData,
    onSuccess,
    onError,
    transformRes,
    fetchPolicy: FETCH_POLICY.NETWORK_ONLY,
  });
};
